import {
  getTokens,
  isExpired,
  setTokens,
  toTokenSet,
  type AuthTokenResponse,
} from '../auth/tokenStore.ts';

/** Thrown by {@link apiFetch} so callers can branch on HTTP status. */
export class ApiError extends Error {
  // Written out longhand rather than as constructor parameter properties, which
  // `erasableSyntaxOnly` forbids (they emit runtime code from type-position syntax).
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;

  constructor(status: number, statusText: string, body: unknown) {
    super(`${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }

  /** True when the server said "you are not allowed" rather than "I broke". */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

const jsonHeaders = { 'Content-Type': 'application/json' } as const;

export interface ApiFetchOptions extends RequestInit {
  json?: unknown;
  /** Skips the Authorization header. Used by login, register and refresh. */
  anonymous?: boolean;
}

/**
 * Thin fetch wrapper. Deliberately not a generated client: request and response shapes
 * come from the generated OpenAPI types (src/api/schema.gen.ts) while this handles
 * transport concerns — auth header, token refresh, JSON encoding, error normalisation.
 *
 * All requests are same-origin: in development Vite proxies /api to the API container,
 * and in production Caddy serves both from one hostname. That removes CORS entirely.
 */
export async function apiFetch<TResponse>(
  path: string,
  init: ApiFetchOptions = {},
): Promise<TResponse> {
  const response = await send(path, init);

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, await readBody(response));
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as TResponse;
  }

  // Not every successful endpoint returns JSON. Identity's /confirmEmail answers with
  // text/plain, and blindly calling .json() on it throws — which surfaced as "that link
  // didn't work" on a confirmation that had in fact just succeeded. Parse what the
  // server says it sent, not what we assume.
  const contentType = response.headers.get('content-type') ?? '';

  if (!contentType.includes('json')) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

/**
 * The same request, when the answer is bytes rather than JSON.
 *
 * Exists because a browser image load carries no `Authorization` header and this app has
 * no cookies — so `<img src="/api/…">` would simply 401. Fetching through the wrapper
 * gets the bearer token, the proactive refresh and the single 401-retry, and the caller
 * turns the blob into an object URL.
 *
 * Deliberately not folded into `apiFetch`: that one answers `undefined` for anything
 * that is not JSON, which is right for Identity's text/plain replies and would silently
 * throw away an image.
 */
export async function apiBlob(path: string, init: ApiFetchOptions = {}): Promise<Blob> {
  const response = await send(path, init);

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, await readBody(response));
  }

  return await response.blob();
}

async function send(path: string, init: ApiFetchOptions): Promise<Response> {
  const { json, headers, anonymous, ...rest } = init;

  const build = (accessToken: string | null): RequestInit => ({
    ...rest,
    headers: {
      ...(json === undefined ? {} : jsonHeaders),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    ...(json === undefined ? {} : { body: JSON.stringify(json) }),
  });

  if (anonymous) {
    return fetch(path, build(null));
  }

  let tokens = getTokens();

  // Refresh proactively when we already know the token is stale. Saves a guaranteed
  // 401 round trip on a slow connection, which is the connection this app lives on.
  if (tokens && isExpired(tokens)) {
    tokens = await refreshTokens();
  }

  const response = await fetch(path, build(tokens?.accessToken ?? null));

  // A 401 despite a token we believed was valid: the server disagrees (revoked, or our
  // clock is wrong). Refresh once and retry — but only once, or a genuinely rejected
  // session turns into an infinite loop.
  if (response.status === 401 && tokens) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      return fetch(path, build(refreshed.accessToken));
    }
  }

  return response;
}

/**
 * A usable access token for a request this wrapper is not making.
 *
 * The SignalR handshake is the only such request: it carries the token as a query string
 * rather than a header, so it cannot go through `send`. Reading the token store directly
 * is what it used to do, and that skipped the refresh below entirely — an access token
 * lives about an hour, and a phone resumed after that negotiated with a dead one. The
 * server answered 401, our reconnect policy retried forever with the same dead token, and
 * the indicator sat on "Connecting…" for the rest of the visit.
 *
 * Returns null when there is no session at all, which the caller sends as an empty token
 * so the server refuses it in the ordinary way.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const tokens = getTokens();

  if (!tokens) {
    return null;
  }

  if (!isExpired(tokens)) {
    return tokens.accessToken;
  }

  const refreshed = await refreshTokens();
  return refreshed?.accessToken ?? null;
}

/**
 * Single-flight refresh: several queries firing at once on app start must not each
 * spend the refresh token, because the server rotates it and only the first would win.
 */
let inFlightRefresh: Promise<ReturnType<typeof getTokens>> | null = null;

function refreshTokens() {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

async function performRefresh() {
  const current = getTokens();
  if (!current) {
    return null;
  }

  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    });

    if (!response.ok) {
      setTokens(null);
      return null;
    }

    const next = toTokenSet((await response.json()) as AuthTokenResponse);
    setTokens(next);
    return next;
  } catch {
    // Offline. Keep the tokens: the request will be retried by the offline queue, and
    // signing the user out because they walked into a chest freezer aisle is hostile.
    return current;
  }
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  try {
    return contentType.includes('json') ? await response.json() : await response.text();
  } catch {
    return null;
  }
}
